import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/NotificationBell';
import { Check, Minus, AlertTriangle, Users, Globe, Info } from 'lucide-react';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT        = '#1B5E20';
const VERT_CLAIR  = '#E8F5E9';
const ROUGE       = '#C62828';
const GRIS_BG     = '#f7f7f7';
const BORD        = '#E0E0E0';
const ORANGE      = '#E65100';
const ORANGE_CLAIR = '#FFF3E0';

const PERMISSIONS_META = {
  pointage: {
    label: 'Pointage',
    items: [
      { key: 'scanner_qr_code',      label: 'Scanner QR Code des employés',        desc: 'Valider les pointages par QR Code' },
      { key: 'appel_nominal',         label: 'Faire l\'appel nominal',               desc: 'Pointer les employés en masse' },
      { key: 'modifier_pointage',     label: 'Modifier un pointage existant',        desc: 'Corriger les heures d\'arrivée ou départ' },
      { key: 'voir_presents_absents', label: 'Voir les présents et absents du site', desc: 'Tableau de présence en temps réel' },
    ],
  },
  conges: {
    label: 'Congés et Absences',
    items: [
      { key: 'valider_conges_n1',          label: 'Valider les congés niveau 1',               desc: 'Première validation avant approbation finale' },
      { key: 'approuver_conges_final',     label: 'Approuver définitivement les congés',        desc: 'Décision finale sur les demandes de congé' },
      { key: 'justifier_absences',         label: 'Justifier les absences de son équipe',       desc: 'Soumettre des justificatifs pour les absences' },
      { key: 'recevoir_demandes_maladie',  label: 'Recevoir les demandes de congé maladie',     desc: 'Traiter les certificats médicaux' },
    ],
  },
  employes: {
    label: 'Gestion Employés',
    items: [
      { key: 'voir_subordonnes',       label: 'Voir la liste de ses subordonnés',         desc: 'Accès à la liste des employés sous sa responsabilité' },
      { key: 'voir_fiche_subordonnes', label: 'Voir la fiche complète d\'un subordonné',  desc: 'Accès aux informations personnelles et professionnelles' },
      { key: 'modifier_subordonnes',   label: 'Modifier les données d\'un subordonné',    desc: 'Éditer les informations des membres de l\'équipe' },
      { key: 'voir_salaires',          label: 'Voir les données salariales',               desc: 'Accès aux salaires et indemnités' },
    ],
  },
  rapports: {
    label: 'Rapports et Export',
    items: [
      { key: 'exporter_donnees',        label: 'Exporter les données Excel et PDF',  desc: 'Générer des exports depuis les listes' },
      { key: 'voir_rapports_globaux',   label: 'Accéder aux rapports globaux',        desc: 'Tableaux de bord et statistiques RH' },
      { key: 'voir_provisions_retraite',label: 'Voir les provisions pour retraite',   desc: 'Données financières de prévoyance' },
      { key: 'voir_heures_sup',         label: 'Voir les heures supplémentaires',     desc: 'Accès aux récapitulatifs d\'heures sup' },
    ],
  },
  temps: {
    label: 'Gestion du temps',
    items: [
      { key: 'peut_voir_temps_site',  label: 'Consulter les semaines et récapitulatifs', desc: 'Semaines, récapitulatifs mensuels et annuels' },
      { key: 'peut_saisir_heures',    label: 'Saisir ou corriger des heures',             desc: 'Saisie manuelle des heures d\'un salarié' },
      { key: 'peut_cloturer_semaine', label: 'Clôturer une semaine',                       desc: 'Uniquement pour un site dont le poste a la charge entière' },
      { key: 'peut_exporter_temps',   label: 'Exporter les tableaux de temps',             desc: 'Exports Excel et PDF' },
      { key: 'peut_voir_tous_temps',  label: 'Voir le temps de tous les sites',            desc: 'Remplacée par la portée globale explicite du poste' },
    ],
  },
  dettes: {
    label: 'Prêts et Retenues',
    items: [
      { key: 'peut_creer_dette',          label: 'Créer une demande de prêt/retenue',   desc: 'Soumettre de nouvelles demandes pour les employés' },
      { key: 'peut_valider_dette',         label: 'Valider une demande',                 desc: 'Approuver ou refuser les demandes en attente' },
      { key: 'peut_modifier_mensualite',   label: 'Modifier la mensualité',              desc: 'Ajuster le montant mensuel prélevé' },
      { key: 'peut_suspendre_dette',       label: 'Suspendre / Reprendre une dette',     desc: 'Mettre en pause ou relancer une retenue active' },
      { key: 'peut_enregistrer_paiement',  label: 'Enregistrer un paiement',             desc: 'Saisir les remboursements mensuels' },
      { key: 'peut_voir_solvabilite',      label: 'Voir la solvabilité d\'un employé',   desc: 'Consulter le taux d\'endettement avant création' },
      { key: 'peut_exporter_dettes',       label: 'Exporter les données dettes',         desc: 'Générer des exports Excel/PDF du module prêts' },
    ],
  },
  ipm: {
    label: 'IPM — habilitations explicites (aucun rôle ne les reçoit automatiquement)',
    // 4 octobre 2026 : Agent / Gestionnaire IPM (gestion, supervision, données médicales) ne
    // s'attribuent plus par le poste, mais nominativement dans « Équipe IPM ».
    note: (
      <>
        Agent IPM et Gestionnaire IPM s'attribuent nominativement dans{' '}
        <Link to="/ipm/equipe" style={{ color: VERT, fontWeight: 600 }}>Équipe IPM</Link>, sans changer le poste.
        Restent ici les contrôles RH, paie et finance et la commission.
      </>
    ),
    items: [
      { key: 'ipm_controle_rh',        label: 'Contrôle RH des dettes et retenues',        desc: 'Salarié → dette → retenue → reste, sans aucune donnée médicale' },
      { key: 'ipm_controle_paie',      label: 'Contrôle paie IPM',                         desc: 'Montant applicable, retour du montant réellement retenu, sans donnée médicale' },
      { key: 'ipm_controle_financier', label: 'Contrôle financier IPM',                    desc: 'Situation financière et vue nominative financière, sans donnée médicale' },
      { key: 'ipm_commission',         label: 'Commission IPM (indicateurs agrégés)',      desc: 'Totaux et évolutions ; les valeurs calculées sur trop peu de salariés sont masquées (seuil provisoire, en attente de décision)' },
    ],
  },
  prets_sogas: {
    label: 'Prêts SOGAS',
    items: [
      { key: 'peut_soumettre_pret_sogas',    label: 'Soumettre une demande de prêt',       desc: 'Permettre à un employé de déposer une demande de prêt sans intérêt' },
      { key: 'peut_approuver_pret_sogas',    label: 'Approuver ou refuser une demande',    desc: 'Valider ou rejeter les demandes de prêt (étape Direction Générale)' },
      { key: 'peut_traiter_pret_sogas',      label: 'Traiter une demande approuvée',       desc: 'Saisir les informations RH et générer le bon de prêt' },
      { key: 'peut_debloquer_pret_sogas',    label: 'Débloquer les fonds',                 desc: 'Confirmer le décaissement et activer la retenue mensuelle' },
      { key: 'peut_voir_tous_prets_sogas',   label: 'Voir tous les prêts (vue globale)',   desc: 'Consulter l\'ensemble des prêts actifs et historiques de tous les employés' },
    ],
  },
  dossier_salarial: {
    label: 'Dossier salarial (RH-PAIE-1)',
    items: [
      { key: 'peut_voir_dossier_salarial',              label: 'Voir le dossier salarial',              desc: 'Consulter la synthèse salariale actuelle d\'un employé — DRH/RH principal/assistant RH y ont accès par défaut' },
      { key: 'peut_voir_historique_salarial',            label: 'Voir l\'historique salarial',            desc: 'Consulter toutes les versions salariales passées, en attente et annulées' },
      { key: 'peut_modifier_dossier_salarial',           label: 'Créer une nouvelle version salariale',   desc: 'Proposer un changement de salaire ou de catégorie avec date d\'effet et motif — DRH/RH principal par défaut' },
      { key: 'peut_valider_dossier_salarial',             label: 'Valider ou annuler une version',         desc: 'Approuver définitivement (ou annuler) une version créée par un tiers — jamais par son propre auteur — DRH uniquement par défaut' },
      { key: 'peut_voir_qualite_dossiers_salariaux',     label: 'Accéder au centre de qualité',            desc: 'Vue globale des dossiers complets, incomplets et en attente de validation' },
      { key: 'peut_exporter_dossier_salarial',            label: 'Exporter les dossiers salariaux',        desc: 'Télécharger un export du centre de qualité — DRH/RH principal par défaut' },
    ],
  },
  carriere: {
    label: 'Carrière (affectation, augmentation, promotion — RH-PAIE-2I)',
    items: [
      { key: 'peut_creer_affectation',       label: 'Créer une affectation',                desc: 'Changer le site, le département, le service ou le poste d\'un employé — DRH/RH principal/assistant RH par défaut' },
      { key: 'peut_creer_augmentation',      label: 'Créer une augmentation/prime/indemnité', desc: 'Proposer une augmentation de sursalaire, une prime ou une indemnité individuelle — DRH/RH principal/assistant RH par défaut' },
      { key: 'peut_creer_promotion',         label: 'Créer une promotion',                   desc: 'Proposer un changement de poste, de famille, de catégorie ou d\'échelon — DRH/RH principal/assistant RH par défaut' },
      { key: 'peut_corriger_donnee_salariale', label: 'Correction administrative de donnée salariale', desc: 'Corriger directement une donnée salariale erronée avec motif et référence obligatoires — DRH uniquement par défaut' },
    ],
  },
  paie_parametres: {
    label: 'Paramètres de paie (RH-PAIE-2)',
    items: [
      { key: 'peut_voir_parametres_paie',          label: 'Voir les paramètres de paie',         desc: 'Consulter le référentiel versionné (taux, plafonds, seuils…) — DRH/RH principal/assistant RH par défaut' },
      { key: 'peut_modifier_parametre_paie',       label: 'Créer/modifier un paramètre',         desc: 'Proposer un nouveau paramètre ou une nouvelle version, en brouillon — DRH/RH principal par défaut' },
      { key: 'peut_valider_parametre_paie',        label: 'Valider un paramètre',                desc: 'Approuver un paramètre soumis par un tiers, jamais par son propre auteur — DRH uniquement par défaut' },
      { key: 'peut_archiver_parametre_paie',       label: 'Archiver un paramètre',                desc: 'Retirer un paramètre du référentiel actif — DRH uniquement par défaut' },
      { key: 'peut_voir_qualite_parametres_paie',  label: 'Accéder au contrôle qualité des paramètres', desc: 'Vue globale des chevauchements, paramètres à valider et interdits de calcul' },
    ],
  },
  conges_droits: {
    label: 'Congés — droits, reprise et régularisation (moteur des droits)',
    note: 'Portée « toute l\'entreprise » requise. Jamais sur sa propre situation ; une proposition est toujours décidée par une autre personne. Ces droits ne donnent pas accès à la configuration des règles.',
    items: [
      { key: 'consulter_droits_conges',   label: 'Consulter les droits à congé', desc: 'Droits ouverts, en acquisition, échéances, données à confirmer et « Voir le calcul » de tous les salariés' },
      { key: 'reprendre_donnees_conges',  label: 'Reprendre les données', desc: 'Proposer ou valider une reprise des anciens soldes, une ancienneté reprise (SERAS) ; qualifier une date d\'entrée' },
      { key: 'regulariser_conges',        label: 'Régulariser', desc: 'Confirmer un droit sous réserve, constater un report, décider une régularisation, préparer une sortie' },
      { key: 'consulter_preparation_paie', label: 'Consulter la préparation future paie', desc: 'Faits RH validés produits pour la future paie (quantités, aucun montant)' },
    ],
  },
  configuration: {
    label: 'Paramètres — Centre de configuration',
    note: 'Portée « toute l\'entreprise » requise. La personne qui prépare une règle ne peut jamais la valider (contrôle du serveur).',
    items: [
      { key: 'config_preparer', label: 'Fonction « Préparateur des paramètres »', desc: 'Préparer une nouvelle version de règle, modifier un brouillon, simuler l\'impact et soumettre, dans tous les domaines du Centre' },
      { key: 'config_valider',  label: 'Fonction « Valideur des paramètres »',    desc: 'Contrôler la simulation, valider ou refuser une règle préparée par une autre personne' },
      { key: 'config_lire',     label: 'Consulter toutes les règles',             desc: 'Lecture seule des règles, de leur historique et de leurs sources' },
      { key: 'config_audit',    label: 'Consulter le journal des règles',         desc: 'Qui a préparé, validé, refusé ou retiré une règle, quand et pourquoi' },
      { key: 'config_temps_preparer', label: 'Temps et pointage : préparer', desc: 'Droit limité à ce domaine' },
      { key: 'config_temps_valider',  label: 'Temps et pointage : valider',  desc: 'Droit limité à ce domaine' },
      { key: 'config_conges_preparer', label: 'Congés et absences : préparer', desc: 'Droit limité à ce domaine' },
      { key: 'config_conges_valider',  label: 'Congés et absences : valider',  desc: 'Droit limité à ce domaine' },
      { key: 'config_heures_sup_preparer', label: 'Heures supplémentaires : préparer', desc: 'Droit limité à ce domaine' },
      { key: 'config_heures_sup_valider',  label: 'Heures supplémentaires : valider',  desc: 'Droit limité à ce domaine' },
      { key: 'config_reglementation_preparer', label: 'Réglementation : préparer une version réglementaire', desc: 'Packs datés (loi, convention, accord)' },
      { key: 'config_reglementation_valider',  label: 'Réglementation : valider une version réglementaire',  desc: 'Validation en bloc, jamais par l\'auteur du pack ni d\'une de ses règles' },
    ],
  },
  application_mobile: {
    label: 'Application mobile SOGAS Employé',
    note: 'Portée « toute l\'entreprise » requise. La personne qui prépare une version ne peut jamais la valider (contrôle du serveur et de la base).',
    items: [
      { key: 'gerer_versions_mobile', label: 'Gérer les versions de l\'application mobile', desc: 'Préparer une version à partir d\'un APK déposé sur le serveur, valider (activer) celle préparée par une autre personne, retirer une version — attribuée à personne par défaut' },
    ],
  },
  temps_reglages: {
    label: 'Temps — réglages (horaires, fériés, bornes)',
    items: [
      { key: 'parametrer_temps',         label: 'Proposer un horaire, une affectation ou un férié', desc: 'Portée « toute l\'entreprise » requise ; vaut aussi « préparer » pour les règles du temps, des congés et des heures supplémentaires' },
      { key: 'valider_parametres_temps', label: 'Valider un horaire ou un férié',                  desc: 'Jamais sa propre proposition ; vaut aussi « valider » pour ces règles' },
      { key: 'gerer_bornes',             label: 'Enrôler ou révoquer une borne de pointage',       desc: 'Portée « toute l\'entreprise » requise' },
    ],
  },
  paie_rubriques: {
    label: 'Rubriques de paie (RH-PAIE-2)',
    items: [
      { key: 'peut_voir_rubriques_paie',      label: 'Voir le catalogue de rubriques',      desc: 'Consulter les rubriques de bulletin et leurs versions — DRH/RH principal/assistant RH par défaut' },
      { key: 'peut_creer_rubrique_paie',      label: 'Créer une rubrique',                   desc: 'Ajouter une nouvelle rubrique au catalogue, en brouillon — DRH/RH principal par défaut' },
      { key: 'peut_modifier_rubrique_paie',   label: 'Créer une version de rubrique',        desc: 'Proposer une nouvelle règle de calcul déclarative pour une rubrique existante — DRH/RH principal par défaut' },
      { key: 'peut_valider_rubrique_paie',    label: 'Valider une rubrique ou une version',  desc: 'Approuver une rubrique ou une version soumise par un tiers — DRH uniquement par défaut' },
      { key: 'peut_archiver_rubrique_paie',   label: 'Archiver une rubrique',                 desc: 'Retirer une rubrique du catalogue actif — DRH uniquement par défaut' },
    ],
  },
  preparation_paie: {
    label: 'Préparation mensuelle de la paie (RH-PAIE-3)',
    items: [
      { key: 'peut_voir_preparation_paie',            label: 'Voir la préparation de la paie',            desc: 'Consulter les périodes, les fiches de préparation et les statuts de complétude — DRH/RH principal/assistant RH par défaut' },
      { key: 'peut_creer_periode_paie',                label: 'Créer/gérer une période de préparation',    desc: 'Créer une période mensuelle et changer son statut technique — DRH/RH principal par défaut' },
      { key: 'peut_preparer_paie',                     label: 'Lancer ou actualiser une préparation',      desc: 'Résoudre l\'état salarial d\'un employé pour une période, sans calculer ni valider de paie — DRH/RH principal par défaut' },
      { key: 'peut_saisir_elements_variables_paie',    label: 'Saisir des éléments variables de paie',     desc: 'Réservé — fonction disponible après activation du moteur des éléments variables de paie' },
    ],
  },
};

const NIVEAUX = Array.from({ length: 10 }, (_, i) => i + 1);

// ── Couleur de bordure selon niveau hiérarchique ──────────────────────
function couleurNiveau(poste) {
  const nom = (poste.intitule || '').toLowerCase();
  if (nom.includes('préposé') || nom.includes('prepose')) return '#C62828';
  const niv = parseInt(poste.niveau_hierarchique) || 5;
  if (niv <= 2) return '#1B5E20';
  if (niv <= 4) return '#388E3C';
  return '#81C784';
}

// ── Construit l'arbre depuis manager_poste_id ─────────────────────────
function buildTree(postes) {
  const map = {};
  postes.forEach(p => { map[p.id] = { ...p, children: [] }; });
  const roots = [];
  postes.forEach(p => {
    if (p.manager_poste_id && map[p.manager_poste_id]) {
      map[p.manager_poste_id].children.push(map[p.id]);
    } else if (!p.manager_poste_id) {
      roots.push(map[p.id]);
    }
  });
  return roots;
}

// ── Carte d'un poste ──────────────────────────────────────────────────
function CartePoste({ poste, sites }) {
  const couleur = couleurNiveau(poste);
  const perms = poste.permissions || {};
  const badges = [
    { key: 'voir_subordonnes',  label: 'Équipe'   },
    { key: 'scanner_qr_code',   label: 'Scanner'  },
    { key: 'valider_conges_n1', label: 'Congés'   },
  ];
  const nbRattaches = parseInt(poste.nb_rattaches) || 0;
  const siteNom = (sites || []).find(s => s.id === poste.site_id)?.nom || null;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E0E0E0',
      borderLeft: `4px solid ${couleur}`,
      borderRadius: 8,
      padding: '8px 12px',
      minWidth: 155,
      maxWidth: 195,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      <div style={{ fontWeight: 700, fontSize: 11, color: '#1a1a1a', lineHeight: 1.3, marginBottom: 3 }}>
        {poste.intitule}
      </div>
      {siteNom && (
        <div style={{ fontSize: 9, color: '#94A3B8', marginBottom: 2, letterSpacing: '0.02em' }}>
          {siteNom}
        </div>
      )}
      <div style={{ fontSize: 10, color: poste.titulaire_nom ? '#555' : '#E65100', marginBottom: 5 }}>
        {poste.titulaire_nom || 'Vacant'}
        {nbRattaches > 0 && (
          <span style={{ marginLeft: 5, background: '#E3F2FD', color: '#1565C0', borderRadius: 10, padding: '0 5px', fontSize: 9, fontWeight: 700 }}>
            {nbRattaches} emp.
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {badges.map(({ key, label }) => (
          <span key={key} style={{
            fontSize: 9, padding: '1px 5px', borderRadius: 8,
            background: perms[key] ? `${couleur}18` : '#f5f5f5',
            color: perms[key] ? couleur : '#bbb',
            fontWeight: perms[key] ? 700 : 400,
            border: `1px solid ${perms[key] ? `${couleur}40` : '#e0e0e0'}`,
          }}>
            {perms[key] ? <Check size={9} style={{ verticalAlign: '-1px' }} /> : <Minus size={9} style={{ verticalAlign: '-1px' }} />} {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Nœud récursif de l'organigramme ──────────────────────────────────
function NoeudOrg({ noeud, sites }) {
  const hasChildren = noeud.children && noeud.children.length > 0;
  const lineColor = '#C8E6C9';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <CartePoste poste={noeud} sites={sites} />
      {hasChildren && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 2, height: 14, background: lineColor }} />
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            {noeud.children.map((enfant, idx) => {
              const isOnly  = noeud.children.length === 1;
              const isFirst = idx === 0;
              const isLast  = idx === noeud.children.length - 1;
              return (
                <div key={enfant.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: '100%', height: 14, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
                    {!isOnly && (
                      <div style={{ position: 'absolute', top: 0, height: 2, background: lineColor, left: isFirst ? '50%' : 0, right: isLast ? '50%' : 0 }} />
                    )}
                    <div style={{ width: 2, height: 14, background: lineColor }} />
                  </div>
                  <NoeudOrg noeud={enfant} sites={sites} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Vue organigramme visuelle ─────────────────────────────────────────
function OrganigrammeView({ postes, sites }) {
  const tree = buildTree(postes);
  const legende = [
    { couleur: '#1B5E20', label: 'Direction (niv. 1-2)' },
    { couleur: '#388E3C', label: 'Management (niv. 3-4)' },
    { couleur: '#81C784', label: 'Encadrement (niv. 5+)' },
    { couleur: '#C62828', label: 'Préposés régionaux' },
  ];
  if (!postes.length) return <div style={{ textAlign: 'center', padding: 40, color: '#bbb' }}>Chargement...</div>;
  return (
    <div>
      <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', padding: '4px 0', minWidth: 'max-content' }}>
          {tree.map(racine => <NoeudOrg key={racine.id} noeud={racine} sites={sites} />)}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 16, paddingTop: 12, borderTop: '1px solid #E0E0E0' }}>
        {legende.map(({ couleur, label }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
            <span style={{ width: 14, height: 5, background: couleur, borderRadius: 2, display: 'inline-block' }} />
            {label}
          </span>
        ))}
        <span style={{ fontSize: 11, color: '#888' }}>Badges : <b>Équipe</b> · <b>Scanner</b> · <b>Congés</b></span>
      </div>
    </div>
  );
}

// ── Conséquences d'une occupation : fonctions ouvertes et population de chacune (Module 0)
function ConsequencesPoste({ consequences }) {
  if (!consequences) return null;
  const { fonctions = [], sans_effet: sansEffet = [], poste } = consequences;
  return (
    <div style={{ background: GRIS_BG, borderRadius: 8, padding: '10px 12px', marginTop: 10 }}>
      {fonctions.length === 0 && <div style={{ fontSize: 12.5, color: '#666' }}>Aucune fonction ouverte par ce poste.</div>}
      {fonctions.map((f) => (
        <div key={f.cle} style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 12.5, alignItems: 'flex-start' }}>
          {f.population.active === false
            ? <AlertTriangle size={13} color={ORANGE} style={{ marginTop: 2, flexShrink: 0 }} />
            : <Check size={13} color={VERT} style={{ marginTop: 2, flexShrink: 0 }} />}
          <div>
            <div style={{ fontWeight: 600, color: '#222' }}>{f.libelle}</div>
            <div style={{ color: f.population.active === false ? ORANGE : '#555', fontSize: 11.5 }}>
              {f.population.libelle}{typeof f.population.nb_personnes === 'number' && f.population.active !== false ? ` — ${f.population.nb_personnes} personne(s)` : ''}
            </div>
          </div>
        </div>
      ))}
      {sansEffet.length > 0 && (
        <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>Cases cochées sans effet : {sansEffet.map((x) => x.libelle).join(', ')}.</div>
      )}
      <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>Calcul pour le poste seul : le rôle de la personne (RH, responsable de site, chef de service…) peut y ajouter sa propre population.</div>
      {poste && poste.portee_globale && (
        <div style={{ fontSize: 11.5, color: '#8D6E00', marginTop: 6, display: 'flex', gap: 4, alignItems: 'center' }}><Globe size={12} /> Portée globale du poste : {poste.portee_globale_motif}</div>
      )}
    </div>
  );
}

export default function PostesPage() {
  const { token, employe: moi } = useAuth();
  const headers   = { Authorization: `Bearer ${token}` };
  // Module 0 : la portée globale d'un poste est réservée au DRH / à l'administrateur.
  const peutPorteeGlobale = ['drh', 'admin'].includes(moi?.role);
  const [catalogue, setCatalogue]     = useState({});
  const [departements, setDepartements] = useState([]);
  const [services, setServices]       = useState([]);
  const [confirmation, setConfirmation] = useState(null); // nomination : conséquences à confirmer
  const [propositions, setPropositions] = useState([]);
  const [motifsRejet, setMotifsRejet] = useState({});

  const [onglet, setOnglet]           = useState('postes');
  const [postes, setPostes]           = useState([]);
  const [employes, setEmployes]       = useState([]);
  const [sites, setSites]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [posteSelectionne, setPosteSelectionne] = useState(null);
  const [editForm, setEditForm]       = useState(null);
  const [recherche, setRecherche]     = useState('');
  const [msg, setMsg]                 = useState(null);
  const [saving, setSaving]           = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const [newForm, setNewForm]         = useState({ code: '', intitule: '', niveau_hierarchique: 5, manager_poste_id: '' });
  const [organigramme, setOrganigramme] = useState([]);
  const [matrice, setMatrice]         = useState({ postes: [], cles: [] });
  const [vueOrg, setVueOrg]           = useState('organigramme');
  const [filtreOrgSite, setFiltreOrgSite] = useState('');

  const showMsg = (type, texte) => {
    setMsg({ type, texte });
    setTimeout(() => setMsg(null), 4000);
  };

  const chargerPostes = useCallback(async () => {
    setLoading(true);
    try {
      const [rPostes, rEmp, rRef, rCat, rDep, rSrv] = await Promise.all([
        axios.get(`${API}/postes`, { headers }),
        axios.get(`${API}/employes?statut=Actif&limite=500`, { headers }),
        axios.get(`${API}/referentiel/sites`, { headers }),
        axios.get(`${API}/postes/catalogue-permissions`, { headers }).catch(() => ({ data: { permissions: [] } })),
        axios.get(`${API}/referentiel/departements`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/referentiel/services`, { headers }).catch(() => ({ data: {} })),
      ]);
      setPostes(rPostes.data.postes || []);
      setEmployes(rEmp.data.employes || []);
      setSites(rRef.data?.sites || []);
      setCatalogue(Object.fromEntries((rCat.data.permissions || []).map((c) => [c.cle, c])));
      setDepartements(rDep.data?.departements || []);
      setServices(rSrv.data?.services || []);
    } catch (e) {
      console.error('chargerPostes:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const chargerOrganigramme = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/postes/organigramme`, { headers });
      setOrganigramme(data.organigramme || []);
    } catch (e) { console.error('organigramme:', e); }
  }, [token]);

  const chargerMatrice = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/postes/matrice`, { headers });
      setMatrice({ postes: data.postes || [], cles: data.cles_permissions || [] });
    } catch (e) { console.error('matrice:', e); }
  }, [token]);

  useEffect(() => { chargerPostes(); }, [chargerPostes]);

  const chargerPropositions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/postes/propositions`, { headers });
      setPropositions(data.propositions || []);
    } catch (e) { setPropositions([]); }
  }, [token]);

  useEffect(() => {
    if (onglet === 'organigramme') chargerOrganigramme();
    if (onglet === 'matrice') chargerMatrice();
    if (onglet === 'propositions') chargerPropositions();
  }, [onglet]);
  useEffect(() => { chargerPropositions(); }, [chargerPropositions]);

  const selectionnerPoste = (p) => {
    setPosteSelectionne(p);
    setEditForm({
      intitule: p.intitule,
      niveau_hierarchique: p.niveau_hierarchique,
      site_id: p.site_id || '',
      departement_id: p.departement_id || '',
      service_id: p.service_id || '',
      manager_poste_id: p.manager_poste_id || '',
      titulaire_id: p.titulaire_id || '',
      portee_globale: p.portee_globale === true,
      portee_globale_motif: p.portee_globale_motif || '',
      permissions: { ...p.permissions },
    });
  };

  const enregistrer = async () => {
    if (!posteSelectionne || !editForm) return;
    setSaving(true);
    try {
      // Mettre à jour le poste
      const corps = {
        intitule: editForm.intitule,
        niveau_hierarchique: parseInt(editForm.niveau_hierarchique),
        site_id: editForm.site_id || null,
        departement_id: editForm.departement_id || null,
        service_id: editForm.service_id || null,
        manager_poste_id: editForm.manager_poste_id || null,
        // Fonctions IPM (Agent / Gestionnaire) : jamais par le poste (refusées par le serveur).
        permissions: Object.fromEntries(Object.entries(editForm.permissions || {})
          .filter(([k]) => !['ipm_gestion', 'ipm_supervision', 'ipm_donnees_medicales'].includes(k))),
      };
      if (peutPorteeGlobale && editForm.portee_globale !== (posteSelectionne.portee_globale === true)) {
        corps.portee_globale = editForm.portee_globale;
        corps.portee_globale_motif = editForm.portee_globale_motif;
      }
      await axios.put(`${API}/postes/${posteSelectionne.id}`, corps, { headers });

      // Module 0 : une nomination ouvre des droits — leurs conséquences sont affichées et
      // confirmées explicitement avant d'être appliquées.
      if (editForm.titulaire_id !== (posteSelectionne.titulaire_id || '')) {
        const { data } = await axios.get(`${API}/postes/${posteSelectionne.id}/consequences`, { headers });
        const titulaire = employes.find((e) => e.id === editForm.titulaire_id);
        setConfirmation({ posteId: posteSelectionne.id, employeId: editForm.titulaire_id || null, titulaire, consequences: data });
        return;
      }

      showMsg('succes', 'Poste enregistré. Les permissions sont applicables immédiatement.');
      await chargerPostes();
      setPosteSelectionne(null);
      setEditForm(null);
    } catch (e) {
      showMsg('erreur', e.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const confirmerNomination = async () => {
    if (!confirmation) return;
    setSaving(true);
    try {
      await axios.put(`${API}/postes/${confirmation.posteId}/titulaire`, { employe_id: confirmation.employeId }, { headers });
      showMsg('succes', confirmation.employeId ? 'Nomination enregistrée : les droits du poste sont actifs.' : 'Poste libéré : ses droits sont retirés.');
      setConfirmation(null);
      await chargerPostes();
      setPosteSelectionne(null);
      setEditForm(null);
    } catch (e) {
      showMsg('erreur', e.response?.data?.message || 'Erreur lors de la nomination');
    } finally { setSaving(false); }
  };

  const deciderProposition = async (pr, accepter) => {
    try {
      if (accepter) {
        await axios.post(`${API}/postes/propositions/${pr.id}/accepter`, {}, { headers });
      } else {
        await axios.post(`${API}/postes/propositions/${pr.id}/rejeter`, { motif: motifsRejet[pr.id] || '' }, { headers });
      }
      showMsg('succes', accepter ? `${pr.employe_nom} occupe désormais le poste ${pr.poste_intitule}.` : 'Proposition rejetée.');
      await Promise.all([chargerPropositions(), chargerPostes()]);
    } catch (e) {
      showMsg('erreur', e.response?.data?.message || 'Erreur');
    }
  };

  const creerPoste = async () => {
    if (!newForm.code || !newForm.intitule) return showMsg('erreur', 'Code et intitulé requis');
    try {
      await axios.post(`${API}/postes`, newForm, { headers });
      showMsg('succes', 'Poste créé');
      setShowModal(false);
      setNewForm({ code: '', intitule: '', niveau_hierarchique: 5, manager_poste_id: '' });
      await chargerPostes();
    } catch (e) {
      showMsg('erreur', e.response?.data?.message || 'Erreur');
    }
  };

  const postesFiltres = postes.filter(p =>
    `${p.intitule} ${p.code}`.toLowerCase().includes(recherche.toLowerCase())
  );

  const nbPerms = (p) => p.permissions ? Object.values(p.permissions).filter(Boolean).length : 0;

  // ── Rendu onglet Postes
  const renderPostes = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, alignItems: 'start' }}>
      {/* Colonne gauche */}
      <div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input
            value={recherche} onChange={e => setRecherche(e.target.value)}
            placeholder="Rechercher un poste..."
            style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${BORD}`, fontSize: 13, outline: 'none' }}
          />
          <button onClick={() => setShowModal(true)}
            style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
            + Nouveau
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>Chargement...</div>
          ) : postesFiltres.map(p => {
            const selectionne = posteSelectionne?.id === p.id;
            return (
              <div key={p.id} onClick={() => selectionnerPoste(p)}
                style={{ background: selectionne ? VERT_CLAIR : '#fff', border: `2px solid ${selectionne ? VERT : BORD}`, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', transition: 'all 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: selectionne ? VERT : '#1a1a1a' }}>{p.intitule}</div>
                  {p.est_nomination === false && (
                    <span style={{ fontSize: 9, fontWeight: 700, background: '#ECEFF1', color: '#546E7A', border: '1px solid #B0BEC5', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                      Poste système
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Niv. {p.niveau_hierarchique} · {p.titulaire_nom || <span style={{ color: ORANGE }}>Vacant</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <span style={{ fontSize: 10, color: VERT }}>{nbPerms(p)} permissions actives</span>
                  {parseInt(p.nb_rattaches) > 0 && (
                    <span style={{ fontSize: 10, color: '#1565C0', background: '#E3F2FD', borderRadius: 10, padding: '0 6px' }}>
                      {p.nb_rattaches} employé{parseInt(p.nb_rattaches) > 1 ? 's' : ''} rattaché{parseInt(p.nb_rattaches) > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Colonne droite */}
      {editForm && posteSelectionne ? (
        <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${BORD}`, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a1a', marginBottom: 16 }}>{posteSelectionne.code} — Édition</div>

          {/* Champs de base */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={s.label}>Intitulé du poste</label>
              <input value={editForm.intitule} onChange={e => setEditForm(f => ({ ...f, intitule: e.target.value }))}
                style={s.input} />
            </div>
            <div>
              <label style={s.label}>Niveau hiérarchique</label>
              <select value={editForm.niveau_hierarchique} onChange={e => setEditForm(f => ({ ...f, niveau_hierarchique: e.target.value }))} style={s.input}>
                {NIVEAUX.map(n => <option key={n} value={n}>Niveau {n}{n === 1 ? ' (Sommet)' : n === 10 ? ' (Base)' : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Poste manager</label>
              <select value={editForm.manager_poste_id} onChange={e => setEditForm(f => ({ ...f, manager_poste_id: e.target.value }))} style={s.input}>
                <option value="">— Aucun (racine) —</option>
                {postes.filter(p => p.id !== posteSelectionne.id && p.est_nomination !== false).map(p => (
                  <option key={p.id} value={p.id}>{p.intitule}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={s.label}>Site de rattachement</label>
              <select value={editForm.site_id} onChange={e => setEditForm(f => ({ ...f, site_id: e.target.value, departement_id: '', service_id: '' }))} style={s.input}>
                <option value="">— Non rattaché —</option>
                {sites.filter(x => x.actif !== false || x.id === editForm.site_id).map(x => <option key={x.id} value={x.id}>{x.code ? `${x.code} — ${x.nom}` : x.nom}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Département de rattachement</label>
              <select value={editForm.departement_id} onChange={e => setEditForm(f => ({ ...f, departement_id: e.target.value, service_id: '' }))} style={s.input}>
                <option value="">— Non rattaché —</option>
                {departements.filter(d => (d.actif !== false || d.id === editForm.departement_id) && (!d.site_id || !editForm.site_id || d.site_id === editForm.site_id)).map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>Service de rattachement</label>
              <select value={editForm.service_id} onChange={e => setEditForm(f => ({ ...f, service_id: e.target.value }))} style={s.input}>
                <option value="">— Non rattaché —</option>
                {services.filter(v => (v.actif !== false || v.id === editForm.service_id) && (!v.departement_id || !editForm.departement_id || v.departement_id === editForm.departement_id)).map(v => <option key={v.id} value={v.id}>{v.nom}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: '#666', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
              <Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Le rattachement définit la population sur laquelle s'exercent les fonctions du poste (pointage, temps, carrière). Sans rattachement au niveau requis, la fonction ne s'applique à personne.</span>
            </div>
            <div style={{ gridColumn: '1 / -1', border: `1px solid ${editForm.portee_globale ? '#FFCC80' : BORD}`, background: editForm.portee_globale ? '#FFF8E1' : '#fff', borderRadius: 8, padding: '10px 12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: '#333', cursor: peutPorteeGlobale ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={editForm.portee_globale} disabled={!peutPorteeGlobale}
                  onChange={e => setEditForm(f => ({ ...f, portee_globale: e.target.checked }))} />
                <Globe size={14} /> Portée globale (toute l'entreprise)
              </label>
              <div style={{ fontSize: 11.5, color: '#666', marginTop: 4 }}>
                À réserver aux fonctions centrales (paie, prêts, comptabilité). Attribution explicite et motivée, réservée au DRH ou à l'administrateur. Ne s'applique jamais aux actes managériaux (congés N1, collaborateurs), toujours limités aux collaborateurs directs.
              </div>
              {editForm.portee_globale && (
                <input value={editForm.portee_globale_motif} disabled={!peutPorteeGlobale} placeholder="Motif de la portée globale (obligatoire)"
                  onChange={e => setEditForm(f => ({ ...f, portee_globale_motif: e.target.value }))} style={{ ...s.input, marginTop: 8 }} />
              )}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={s.label}>Titulaire actuel</label>
              <select value={editForm.titulaire_id} onChange={e => setEditForm(f => ({ ...f, titulaire_id: e.target.value }))} style={s.input}>
                <option value="">— Vacant —</option>
                {employes.map(e => <option key={e.id} value={e.id}>{e.prenom} {e.nom} ({e.matricule})</option>)}
              </select>
            </div>
          </div>

          {/* Permissions groupées */}
          {Object.entries(PERMISSIONS_META).map(([cat, { label, items, note }]) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>{label}</div>
              {note && (
                <div style={{ fontSize: 12, color: '#555', background: VERT_CLAIR, borderRadius: 8, padding: '8px 12px', marginBottom: 8, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true" /><span>{note}</span>
                </div>
              )}
              <div style={{ background: GRIS_BG, borderRadius: 10, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map(({ key, label: lbl, desc }) => {
                  const actif = editForm.permissions[key] || false;
                  // Module 0 : une case sans effet ne peut pas être activée (seulement désactivée),
                  // et chaque droit annonce sa population.
                  const info = catalogue[key];
                  const sansEffet = info && info.statut === 'sans_effet';
                  const modifiable = !sansEffet || actif;
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: sansEffet ? 0.6 : 1 }}>
                      <div onClick={() => modifiable && setEditForm(f => ({ ...f, permissions: { ...f.permissions, [key]: !actif } }))}
                        title={sansEffet ? 'Case sans effet aujourd\'hui' : undefined}
                        style={{ width: 40, height: 22, borderRadius: 11, background: actif ? VERT : '#ccc', cursor: modifiable ? 'pointer' : 'not-allowed', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                        <div style={{ position: 'absolute', top: 3, left: actif ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: actif ? 600 : 400, color: actif ? '#1a1a1a' : '#666' }}>{lbl}</div>
                        <div style={{ fontSize: 11, color: '#999' }}>{desc}</div>
                        {info && (
                          <div style={{ fontSize: 11, color: sansEffet ? ORANGE : '#1565C0', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {sansEffet ? <AlertTriangle size={11} /> : <Users size={11} />}
                            {sansEffet ? `Sans effet aujourd'hui — prévue module ${info.module}` : `Population : ${info.population}`}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: actif ? VERT : '#bbb' }}>{actif ? 'ON' : 'OFF'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Bouton enregistrer */}
          <div style={{ marginTop: 4 }}>
            <button onClick={enregistrer} disabled={saving}
              style={{ background: saving ? '#aaa' : VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
              Les modifications s'appliquent immédiatement au titulaire actuel.
            </div>
          </div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${BORD}`, padding: 40, textAlign: 'center', color: '#bbb', fontSize: 14 }}>
          Sélectionnez un poste pour l'éditer
        </div>
      )}
    </div>
  );

  // ── Rendu nœud organigramme (récursif)
  const renderNoeud = (noeud, profondeur = 0) => (
    <div key={noeud.id} style={{ marginLeft: profondeur * 24 }}>
      <div
        onClick={() => { setOnglet('postes'); setTimeout(() => { const p = postes.find(x => x.id === noeud.id); if (p) selectionnerPoste(p); }, 100); }}
        style={{ background: noeud.titulaire_nom ? VERT_CLAIR : ORANGE_CLAIR, border: `2px solid ${noeud.titulaire_nom ? VERT : ORANGE}`, borderRadius: 10, padding: '8px 14px', marginBottom: 6, cursor: 'pointer', display: 'inline-block', minWidth: 200 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: noeud.titulaire_nom ? VERT : ORANGE }}>{noeud.intitule}</div>
        <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{noeud.titulaire_nom || 'Vacant'}</div>
      </div>
      {noeud.subordonnes?.length > 0 && (
        <div style={{ borderLeft: `2px solid ${BORD}`, marginLeft: 16, paddingLeft: 8, marginBottom: 4 }}>
          {noeud.subordonnes.map(s => renderNoeud(s, 0))}
        </div>
      )}
    </div>
  );

  // ── Rendu onglet Organigramme
  const renderOrganigramme = () => {
    const postesFiltres = filtreOrgSite
      ? postes.filter(p => p.site_id === filtreOrgSite)
      : postes;
    const orgVide = filtreOrgSite && postesFiltres.length === 0;
    return (
      <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${BORD}`, padding: 24, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
        {/* Header avec filtre site + toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a' }}>Arbre hiérarchique des postes</div>
            <select
              value={filtreOrgSite}
              onChange={e => setFiltreOrgSite(e.target.value)}
              style={{
                height: 44, padding: '0 12px',
                border: `1px solid ${BORD}`, borderRadius: 8,
                fontSize: 12, color: filtreOrgSite ? VERT : '#555',
                fontWeight: filtreOrgSite ? 700 : 400,
                background: '#fff', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">Tous les sites</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>{s.nom}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 0, border: `1px solid ${BORD}`, borderRadius: 8, overflow: 'hidden' }}>
            {[{ k: 'liste', label: 'Liste' }, { k: 'organigramme', label: 'Organigramme' }].map(v => (
              <button key={v.k} onClick={() => setVueOrg(v.k)}
                style={{ padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: vueOrg === v.k ? 700 : 400,
                  background: vueOrg === v.k ? VERT : '#fff', color: vueOrg === v.k ? '#fff' : '#666', transition: 'all 0.15s' }}>
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {/* Vue Liste — existante intacte */}
        {vueOrg === 'liste' && (
          <>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ width: 12, height: 12, background: VERT, borderRadius: 3 }} />Avec titulaire
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ width: 12, height: 12, background: ORANGE, borderRadius: 3 }} />Vacant
              </span>
            </div>
            {organigramme.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#bbb' }}>Chargement...</div>
            ) : (
              organigramme.map(n => renderNoeud(n))
            )}
          </>
        )}

        {/* Vue Organigramme — filtre plat client-side sur postes */}
        {vueOrg === 'organigramme' && (
          orgVide
            ? <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8', fontSize: 13 }}>Aucun poste rattaché à ce site.</div>
            : <OrganigrammeView postes={postesFiltres} sites={sites} />
        )}
      </div>
    );
  };

  // ── Rendu onglet Matrice
  const renderMatrice = () => {
    const cleLbl = {
      scanner_qr_code: 'QR', appel_nominal: 'Appel', modifier_pointage: 'Mod. pt.',
      voir_presents_absents: 'Présents', valider_conges_n1: 'Congés N1', approuver_conges_final: 'Congés Final',
      justifier_absences: 'Justify', recevoir_demandes_maladie: 'Maladie',
      voir_subordonnes: 'Subord.', voir_fiche_subordonnes: 'Fiche', modifier_subordonnes: 'Mod. sub.',
      voir_salaires: 'Salaire', voir_heures_sup: 'H.Sup', exporter_donnees: 'Export',
      voir_rapports_globaux: 'Rapports', voir_provisions_retraite: 'Retraite',
    };
    return (
      <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${BORD}`, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 6 }}>Matrice globale des permissions</div>
        <div style={{ fontSize: 11.5, color: '#888', marginBottom: 14 }}>Les colonnes grisées en italique sont des cases sans effet aujourd'hui (fonction prévue dans un module ultérieur).</div>
        {matrice.postes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#bbb' }}>Chargement...</div>
        ) : (
          <table style={{ borderCollapse: 'collapse', fontSize: 11, whiteSpace: 'nowrap' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `2px solid ${BORD}`, color: '#555', minWidth: 160 }}>Poste</th>
                {matrice.cles.map(cle => {
                  const sansEffet = catalogue[cle]?.statut === 'sans_effet';
                  return (
                    <th key={cle} style={{ padding: '6px 8px', textAlign: 'center', borderBottom: `2px solid ${BORD}`, color: sansEffet ? '#bbb' : '#666', fontWeight: 600, fontSize: 10, cursor: 'pointer', fontStyle: sansEffet ? 'italic' : 'normal' }}
                      title={sansEffet ? `${cle} — sans effet aujourd'hui (module ${catalogue[cle].module})` : `${cle} — ${catalogue[cle]?.population || ''}`}>{cleLbl[cle] || cle}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {matrice.postes.map((p, i) => (
                <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : GRIS_BG }}
                  onClick={() => { setOnglet('postes'); setTimeout(() => { const ps = postes.find(x => x.id === p.id); if (ps) selectionnerPoste(ps); }, 100); }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600, color: '#1a1a1a', cursor: 'pointer' }}>
                    {p.intitule}
                    <div style={{ fontSize: 10, color: '#999', fontWeight: 400 }}>{p.titulaire_nom || 'Vacant'}</div>
                  </td>
                  {matrice.cles.map(cle => {
                    const actif = p.permissions?.[cle];
                    return (
                      <td key={cle} style={{ padding: '8px', textAlign: 'center', cursor: 'pointer' }}>
                        {actif
                          ? <Check size={13} color={catalogue[cle]?.statut === 'sans_effet' ? '#bbb' : VERT} />
                          : <Minus size={13} color="#ddd" />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  // ── Rendu onglet Propositions (import) — jamais d'effet sans décision explicite
  const renderPropositions = () => (
    <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${BORD}`, padding: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a1a', marginBottom: 4 }}>Propositions d'occupation issues d'un import</div>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 16 }}>Un import ne donne jamais de droits : chaque poste reconnu est proposé ici et reste sans effet tant qu'il n'est pas accepté.</div>
      {propositions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#999', fontSize: 13 }}>Aucune proposition en attente.</div>
      ) : propositions.map((pr) => (
        <div key={pr.id} style={{ border: `1px solid ${BORD}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{pr.employe_nom} <span style={{ color: '#999', fontWeight: 400, fontSize: 12 }}>({pr.matricule})</span></div>
              <div style={{ fontSize: 12.5, color: '#444', marginTop: 2 }}>Poste proposé : <b>{pr.poste_intitule}</b> ({pr.poste_code}) — à compter du {new Date(pr.date_effet).toLocaleDateString('fr-FR')}</div>
              <div style={{ fontSize: 11.5, color: '#888', marginTop: 2 }}>Source : {pr.reference || 'import'}{pr.cree_par_nom ? ` — importé par ${pr.cree_par_nom}` : ''}</div>
              {pr.titulaire_actuel_nom && (
                <div style={{ fontSize: 11.5, color: ORANGE, marginTop: 4, display: 'flex', gap: 4, alignItems: 'center' }}><AlertTriangle size={12} /> Le titulaire actuel ({pr.titulaire_actuel_nom}) sera remplacé.</div>
              )}
            </div>
          </div>
          <ConsequencesPoste consequences={pr.consequences} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
            <button onClick={() => deciderProposition(pr, true)} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 12.5 }}>Accepter et nommer</button>
            <input value={motifsRejet[pr.id] || ''} onChange={(e) => setMotifsRejet((m) => ({ ...m, [pr.id]: e.target.value }))} placeholder="Motif du rejet (obligatoire pour rejeter)" style={{ ...s.input, width: 280, flex: '0 1 280px' }} />
            <button onClick={() => deciderProposition(pr, false)} disabled={(motifsRejet[pr.id] || '').trim().length < 5}
              style={{ background: '#fff', color: ROUGE, border: `1px solid #FFCDD2`, borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 12.5 }}>Rejeter</button>
          </div>
        </div>
      ))}
    </div>
  );

  const aujourd_hui = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <Layout>
      <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', minHeight: '100vh', background: GRIS_BG }}>

        {/* Header vert */}
        <div className="sg-bandeau-clair" style={{ background: VERT, padding: '14px 18px 20px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, position: 'relative', zIndex: 1 }}>
            <img src="/logo-sogas1.png" alt="SOGAS" style={{ height: 28, objectFit: 'contain' }}
              onError={e => { e.target.onerror = null; e.target.src = '/logo-sogas.png'; }} />
            <NotificationBell couleurIcone="#fff" taille={18} />
          </div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <p style={{ margin: '0 0 2px', color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>Gestion des Postes</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem', textTransform: 'capitalize' }}>{aujourd_hui}</p>
              <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 8px', color: '#fff', fontSize: '0.58rem', fontWeight: 600 }}>RH</span>
            </div>
          </div>
        </div>

        {/* Message */}
        {msg && (
          <div style={{ margin: '12px 16px 0', padding: '10px 16px', borderRadius: 8, fontWeight: 600, fontSize: 13,
            background: msg.type === 'succes' ? VERT_CLAIR : '#FFEBEE',
            color: msg.type === 'succes' ? VERT : ROUGE,
            border: `1px solid ${msg.type === 'succes' ? '#A5D6A7' : '#FFCDD2'}` }}>
            {msg.texte}
          </div>
        )}

        {/* Onglets */}
        <div style={{ display: 'flex', background: '#fff', borderBottom: `2px solid ${BORD}`, padding: '0 16px' }}>
          {[
            { k: 'postes',         label: 'Postes et Permissions' },
            { k: 'organigramme',   label: 'Organigramme' },
            { k: 'matrice',        label: 'Matrice globale' },
            { k: 'propositions',   label: `Propositions d'import${propositions.length ? ` (${propositions.length})` : ''}` },
          ].map(o => (
            <button key={o.k} onClick={() => setOnglet(o.k)}
              style={{ padding: '12px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: onglet === o.k ? 700 : 400, color: onglet === o.k ? VERT : '#666', borderBottom: onglet === o.k ? `3px solid ${VERT}` : '3px solid transparent', marginBottom: -2, transition: 'all 0.15s' }}>
              {o.label}
            </button>
          ))}
        </div>

        {/* Corps */}
        <div style={{ padding: 16 }}>
          {onglet === 'postes'       && renderPostes()}
          {onglet === 'organigramme' && renderOrganigramme()}
          {onglet === 'matrice'      && renderMatrice()}
          {onglet === 'propositions' && renderPropositions()}
        </div>
      </div>

      {/* Module 0 : confirmation d'une nomination — les droits ouverts sont affichés */}
      {confirmation && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a1a', marginBottom: 6 }}>
              {confirmation.employeId ? 'Confirmer la nomination' : 'Confirmer la libération du poste'}
            </div>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 12 }}>
              {confirmation.employeId
                ? <>Nommer <b>{confirmation.titulaire ? `${confirmation.titulaire.prenom} ${confirmation.titulaire.nom}` : ''}</b> sur le poste <b>{confirmation.consequences.poste.intitule}</b>. Cette nomination donne accès à :</>
                : <>Le poste <b>{confirmation.consequences.poste.intitule}</b> deviendra vacant ; son titulaire perd les fonctions suivantes :</>}
            </div>
            <ConsequencesPoste consequences={confirmation.consequences} />
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={confirmerNomination} disabled={saving}
                style={{ flex: 1, background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontWeight: 700, cursor: 'pointer' }}>
                {saving ? 'Enregistrement...' : 'Confirmer'}
              </button>
              <button onClick={() => { setConfirmation(null); chargerPostes(); }}
                style={{ flex: 1, background: '#fff', color: '#555', border: `1px solid ${BORD}`, borderRadius: 8, padding: '10px', fontWeight: 600, cursor: 'pointer' }}>
                Ne pas nommer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nouveau poste */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 440 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1a1a1a', marginBottom: 20 }}>Nouveau poste</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={s.label}>Code unique *</label>
                <input value={newForm.code} onChange={e => setNewForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="ex: CHEF_DEPOT" style={s.input} />
              </div>
              <div>
                <label style={s.label}>Intitulé *</label>
                <input value={newForm.intitule} onChange={e => setNewForm(f => ({ ...f, intitule: e.target.value }))}
                  placeholder="ex: Chef de Dépôt" style={s.input} />
              </div>
              <div>
                <label style={s.label}>Niveau hiérarchique</label>
                <select value={newForm.niveau_hierarchique} onChange={e => setNewForm(f => ({ ...f, niveau_hierarchique: parseInt(e.target.value) }))} style={s.input}>
                  {NIVEAUX.map(n => <option key={n} value={n}>Niveau {n}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Poste manager</label>
                <select value={newForm.manager_poste_id} onChange={e => setNewForm(f => ({ ...f, manager_poste_id: e.target.value }))} style={s.input}>
                  <option value="">— Aucun —</option>
                  {postes.filter(p => p.est_nomination !== false).map(p => <option key={p.id} value={p.id}>{p.intitule}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={creerPoste}
                style={{ flex: 1, background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontWeight: 700, cursor: 'pointer' }}>
                Créer le poste
              </button>
              <button onClick={() => setShowModal(false)}
                style={{ flex: 1, background: '#fff', color: '#555', border: `1px solid ${BORD}`, borderRadius: 8, padding: '10px', fontWeight: 600, cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const s = {
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E0E0E0', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' },
};
